import ApiService from '@services/api.service'
import { Notify } from 'quasar'

export const etudiantAca = {
  namespaced: true,
  state: {
    students: [],
    loading: false,
    error: null,
    eligibility: {
      isEligible: false,
      details: null,
      isChecking: false
    }
  },

  mutations: {
    SET_STUDENTS(state, students) {
      state.students = students
    },
    SET_LOADING(state, isLoading) {
      state.loading = isLoading
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    SET_ELIGIBILITY_CHECKING(state, isChecking) {
      state.eligibility.isChecking = isChecking
    },
    SET_ELIGIBILITY_RESULT(state, { eligible, details }) {
      state.eligibility.isEligible = eligible
      state.eligibility.details = details
    }
  },

  actions: {
    /**
     * Vérifie l'éligibilité de l'étudiant pour générer une ACA.
     */
    async checkAcaEligibility({ commit }, studentId) {
      if (!studentId) return

      commit('SET_ELIGIBILITY_CHECKING', true)
      try {
        const response = await ApiService.getRequest(
          `etudiants/${studentId}/check-aca-eligibility`
        )
        commit('SET_ELIGIBILITY_RESULT', {
          eligible: response.data.eligible,
          details: response.data
        })
        return response.data
      } catch (error) {
        console.error("Erreur lors de la vérification d'éligibilité:", error)
        commit('SET_ELIGIBILITY_RESULT', { eligible: true, details: null })
      } finally {
        commit('SET_ELIGIBILITY_CHECKING', false)
      }
    },

    /**
     * Charge la liste des stagiaires.
     */
    async fetchStudents({ commit }) {
      commit('SET_LOADING', true)
      try {
        const response = await ApiService.getRequest(
          'etudiant_attest_compet_acquis?type=1'
        )
        const students = response.data['hydra:member'] || []
        commit('SET_STUDENTS', students)
        return students
      } catch (error) {
        commit('SET_ERROR', 'Erreur lors du chargement des étudiants.')
        console.error('Erreur dans fetchStudents (etudiantAca):', error)
        Notify.create({
          message: 'Erreur lors du chargement des étudiants',
          color: 'negative'
        })
        return []
      } finally {
        commit('SET_LOADING', false)
      }
    },

    /**
     * Charge les détails d'un étudiant.
     */
    async fetchStudentDetails({ commit }, studentId) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const response = await ApiService.getRequest(
          `etudiant_attest_compet_acquis/${studentId}`
        )
        const student = response.data

        // Filtrer pour ne garder que l'ACA active (non archivée)
        if (student.etudiant_acas && student.etudiant_acas.length > 0) {
          const activeAca = student.etudiant_acas
            .filter((aca) => aca.statut !== 'archivee')
            .sort(
              (a, b) =>
                new Date(b.date_generation) - new Date(a.date_generation)
            )[0]

          student.etudiant_acas = activeAca ? [activeAca] : []
        }

        return student
      } catch (error) {
        commit(
          'SET_ERROR',
          "Erreur lors du chargement des détails de l'étudiant."
        )
        console.error('Erreur dans fetchStudentDetails:', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    /**
     * Génère un nouvel ACA pour un étudiant.
     */
    async generateAca({ commit, dispatch }, studentId) {
      commit('SET_LOADING', true)
      try {
        const response = await ApiService.postRequest(
          `etudiants/${studentId}/etudiant_acas`,
          {}
        )

        //Déterminer si c'est une création ou une mise à jour.
        const isUpdate =
          response.data.date_generation !== response.data.created_at

        Notify.create({
          message: isUpdate
            ? 'ACA mise à jour avec succès.'
            : 'Nouvelle ACA générée avec succès en mode brouillon.',
          color: 'positive'
        })

        return await dispatch('fetchStudentDetails', studentId)
      } catch (error) {
        commit('SET_ERROR', "Erreur lors de la génération de l'ACA.")
        console.error('Erreur dans generateAca:', error)

        //TODO: Le message de l'api ne s'affiche pas correctement.
        const errorMessage =
          error.response?.data?.['hydra:description'] ||
          error.response?.data?.message ||
          "Erreur lors de la génération de l'ACA."

        Notify.create({
          message: errorMessage,
          color: 'negative'
        })
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    /**
     * Met à jour le statut de l'ACA.
     */
    async updateAcaStatus(
      { commit, dispatch },
      { acaId, studentId, newStatus }
    ) {
      commit('SET_LOADING', true)
      try {
        if (newStatus === 'finalisee') {
          await ApiService.postRequest(`etudiant_acas/${acaId}/finalize`, {})
        } else {
          await ApiService.putRequest(
            `etudiant_acas/${acaId}`,
            { statut: newStatus },
            { headers: { 'Content-Type': 'application/merge-patch+json' } }
          )
        }

        Notify.create({
          message:
            newStatus === 'finalisee'
              ? "L'ACA a été finalisée avec succès."
              : "Statut de l'ACA mis à jour avec succès.",
          color: 'positive',
          icon: newStatus === 'finalisee' ? 'check_circle' : 'info'
        })

        return await dispatch('fetchStudentDetails', studentId)
      } catch (error) {
        commit('SET_ERROR', 'Erreur lors de la mise à jour du statut.')
        console.error('Erreur dans updateAcaStatus:', error)

        // Afficher le message d'erreur du serveur
        const errorMessage =
          error.response?.data?.['hydra:description'] ||
          error.response?.data?.message ||
          'Erreur lors de la mise à jour du statut.'

        Notify.create({
          message: errorMessage,
          color: 'negative'
        })
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    }
  },

  getters: {
    getStudents: (state) => state.students,
    isLoading: (state) => state.loading,
    getError: (state) => state.error,
    isEligible: (state) => state.eligibility.isEligible,
    isCheckingEligibility: (state) => state.eligibility.isChecking,
    eligibilityDetails: (state) => state.eligibility.details
  }
}
